import React from 'react';
import StoryCard from './StoryCard';
import ArticleCard from './ArticleCard';

const EnhancedBriefingModal = ({ 
  show, 
  briefingData, 
  summaryId, 
  onClose, 
  singleStory = null, 
  isPublic = false,
  authUser = null
}) => {
  if (!show) return null;

  // Helper function to check if processing note should be shown
  const shouldShowProcessingNote = (error) => {
    if (!error || !error.trim()) return false;
    if (!authUser || !authUser.is_admin) return false;
    
    // Filter out "Insufficient articles", "Fewer than", and "all categories" notes
    const filteredError = error
      .split('\n')
      .filter(line => 
        !line.includes('Insufficient articles') && 
        !line.includes('Fewer than') &&
        !line.includes('all categories')
      )
      .join('\n')
      .trim();
    
    return filteredError.length > 0;
  };

  // Split stories into local and national (same logic as Landing.js)
  const normalizeStories = (stories, displayType) => {
    if (!Array.isArray(stories)) return [];
    return stories.map(story => ({
      ...story,
      displayType,
      story_type: story.story_type || story.storyType || story.type || 'insight'
    }));
  };

  const deriveFallbackStories = () => {
    const fallbackLocal = [];
    const fallbackNational = [];

    const pushStories = (collection, storyType) => {
      if (!Array.isArray(collection)) return;
      collection.forEach(story => {
        const article = story.article;
        const isLocal = story.is_local_ai ?? (article?.location?.is_local ?? article?.is_local ?? false);
        const enriched = {
          ...story,
          displayType: isLocal ? 'local' : 'national',
          story_type: story.story_type || storyType || 'insight'
        };
        if (article && article.date) {
          enriched.sortDate = article.date;
        }
        (isLocal ? fallbackLocal : fallbackNational).push(enriched);
      });
    };

    if (briefingData) {
      pushStories(briefingData.top_insights, 'insight');
      pushStories(briefingData.week_stories, 'week');
      pushStories(briefingData.day_stories, 'day');
    }

    return { fallbackLocal, fallbackNational };
  };

  const dedupeStories = (stories) => {
    const seen = new Set();
    return stories.filter(story => {
      const keyBase = story.article?.id || story.article_id || story.id;
      if (!keyBase) return true;
      const key = `${story.displayType || ''}-${keyBase}`;
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  };

  let localStories = normalizeStories(briefingData?.local_stories, 'local');
  let nationalStories = normalizeStories(briefingData?.national_stories, 'national');

  if (localStories.length === 0 && nationalStories.length === 0 && briefingData) {
    const { fallbackLocal, fallbackNational } = deriveFallbackStories();
    localStories = fallbackLocal;
    nationalStories = fallbackNational;
  }

  localStories = dedupeStories(localStories);
  nationalStories = dedupeStories(nationalStories);

  // Sort stories by date (newest first) - same as Landing.js
  const sortStoriesByDate = (stories) => {
    return [...stories].sort((a, b) => {
      const dateA = a.article?.date ? new Date(a.article.date) : new Date(0);
      const dateB = b.article?.date ? new Date(b.article.date) : new Date(0);
      return dateB - dateA;  // Descending order (newest first)
    });
  };

  localStories = sortStoriesByDate(localStories);
  nationalStories = sortStoriesByDate(nationalStories);

  const renderStorySection = (stories, title, displayType, emptyMessage, storyTypeOverride) => {
    if (!stories || stories.length === 0) {
      return (
        <section className="briefing-section">
          <h2 className="briefing-section-title">{title}</h2>
          <p className="text-muted">{emptyMessage}</p>
        </section>
      );
    }

    return (
      <section className="briefing-section">
        <h2 className="briefing-section-title">{title}</h2>
        <div className="stories-container">
          {stories.map((story, index) => (
            <StoryCard
              key={`${displayType}-${story.id || index}`}
              story={story}
              type={displayType}
              storyType={story.story_type || storyTypeOverride || displayType}
              isPublic={isPublic}
              authUser={authUser}
            />
          ))}
        </div>
      </section>
    );
  };

  const renderArticlesSection = () => {
    if (!briefingData?.articles || briefingData.articles.length === 0) {
      return (
        <section className="briefing-section">
          <h2 className="briefing-section-title">Referenced Articles</h2>
          <p className="text-muted">No referenced articles available.</p>
        </section>
      );
    }

    return (
      <section className="briefing-section">
        <h2 className="briefing-section-title">Referenced Articles</h2>
        <div className="articles-container">
          {briefingData.articles.map(article => (
            <ArticleCard
              key={article.id}
              article={article}
              showFullText={!isPublic}
            />
          ))}
        </div>
      </section>
    );
  };

  const renderSingleStory = () => {
    if (!singleStory) return null;

    const storyType = singleStory.type || 'insight';
    const storyData = singleStory.story || singleStory;
    const displayType = singleStory.displayType || storyType;

    return (
      <div className="single-story-container">
        <StoryCard
          story={storyData}
          type={displayType}
          storyType={storyType}
          isPublic={isPublic}
          authUser={authUser}
        />
      </div>
    );
  };

  const renderCopyrightNotice = () => (
    <section className="copyright-section">
      <div className="copyright-notice">
        <h3 className="copyright-title">Copyright Notice</h3>
        <p className="copyright-text">
          This document may contain copyrighted material whose use has not been specifically 
          authorized by the copyright owner. AECI is making this article available in its effort 
          to advance the understanding of energy policy. AECI believes that this constitutes a 
          "fair use" of the copyrighted material as provided for in Section 107 of the U.S. 
          Copyright Law. If you wish to use this copyrighted material for purposes of your own 
          purpose that goes beyond "fair use," you must obtain permission from the copyright owner. 
          If your copyrighted material appears on this article and you disagree with AECI's 
          assessment that it constitutes "fair use," please contact the Communications Team.
        </p>
      </div>
    </section>
  );

  const getModalTitle = () => {
    if (singleStory) {
      const storyData = singleStory.story || singleStory;
      return storyData.article?.title || storyData.ai_takeaway_title || 'Industry Story';
    }
    return briefingData?.ai_title || `Management Briefing (ID: ${summaryId})`;
  };

  const getModalSize = () => {
    return singleStory ? 'modal-lg' : 'modal-fullscreen';
  };

  return (
    <div className="modal show d-block modal-overlay-dark" tabIndex="-1" role="dialog">
      <div className={`modal-dialog ${getModalSize()}`} role="document">
        <div className="modal-content enhanced-briefing-modal">
          <div className="modal-header">
            <h1 className="modal-title">{getModalTitle()}</h1>
            <button 
              type="button" 
              className="modal-close-button" 
              onClick={onClose}
              aria-label="Close"
            >
              <i className="fas fa-times"></i>
            </button>
          </div>
          
          <div className="modal-body">
            {!briefingData ? (
              <div className="modal-loading">
                <div className="loading-spinner">
                  <i className="fas fa-spinner fa-spin fa-2x"></i>
                </div>
                <p>Loading briefing...</p>
              </div>
            ) : (
              <div className="briefing-content">
                {shouldShowProcessingNote(briefingData.error) && (
                  <div className="alert alert-warning briefing-error">
                    <i className="fas fa-exclamation-triangle me-2"></i>
                    <strong>Processing Note:</strong> {briefingData.error
                      .split('\n')
                      .filter(line => 
                        !line.includes('Insufficient articles') && 
                        !line.includes('Fewer than') &&
                        !line.includes('all categories')
                      )
                      .join('\n')
                      .trim()}
                  </div>
                )}

                {singleStory ? (
                  renderSingleStory()
                ) : (
                  <>
                    {renderStorySection(
                      localStories,
                      '📍 Local Stories',
                      'local',
                      'No local stories available.',
                      'insight'
                    )}

                    {renderStorySection(
                      nationalStories,
                      '🌎 National News',
                      'national',
                      'No national news available.',
                      'insight'
                    )}
                    
                    {renderArticlesSection()}
                  </>
                )}

                {renderCopyrightNotice()}
              </div>
            )}
          </div>
          
          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
            {isPublic && (
              <button 
                type="button" 
                className="btn btn-success"
                onClick={() => window.print()}
              >
                <i className="fas fa-print me-2"></i>
                Print
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EnhancedBriefingModal;